import React from 'react';

const SECTION_HEADER = 'product-template-pdf-section-header bg-green-600 text-white px-4 py-2 text-sm font-semibold';
const SOP_SUB_HEADER = 'product-template-pdf-sop-subheader bg-orange-500 text-white px-4 py-2 text-sm font-semibold';
const BORDER_BOX = 'border border-gray-300 bg-white p-2 min-h-[2rem] text-sm text-gray-800';

const handlePrint = () => {
  window.print();
};

/**
 * Renders a read-only preview of the Product Template (PDF-style layout).
 * Used in the preview overlay before download/print.
 */
const PrintProductTemplatePdf = ({
  imagePreview,
  productDetails = {},
  selectedSite,
  addToMenu,
  productType = 'selling',
  bomEntries = [],
  stockItems = [],
  totalCalories = 0,
  perServe = '',
  caloriesPerServe = null,
  sopProcedures = [],
  description = '',
  allergenItems = [],
  allergenData,
}) => {
  const getRawMaterialName = (rawMaterialId) => {
    if (!rawMaterialId) return '';
    const item = stockItems.find(
      (s) => (s.stock_item_id || s.id) === rawMaterialId
    );
    return item ? item.name : '';
  };

  const sopLines = (text) =>
    (text || '')
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);

  const showPerServe = perServe != null && String(perServe).trim() !== '';
  const showCaloriesPerServe =
    caloriesPerServe != null && !Number.isNaN(Number(caloriesPerServe));

  return (
    <div
      className="bg-gray-50 text-gray-900 max-w-[800px] mx-auto product-template-pdf-content"
      id="product-template-pdf-print-area"
    >
      <style>{`
        @media print {
          .product-template-pdf-content {
            max-width: none !important;
            width: 257mm !important;
            margin-left: auto !important;
            margin-right: auto !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .product-template-pdf-content * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .product-template-pdf-section {
            page-break-inside: avoid;
          }
        }
      `}</style>

      {/* Product Information */}
      <div className="product-template-pdf-section mb-4">
        <div className={SECTION_HEADER}>Product Information</div>
        <div className="grid grid-cols-1 md:grid-cols-[354px_1fr] gap-4 border border-t-0 border-gray-300 p-4 bg-white">
          <div className="flex flex-col">
            <div className="h-80 rounded-lg border border-gray-300 bg-gray-100 overflow-hidden flex items-center justify-center">
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Product"
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="text-gray-400 text-sm">No image</span>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <div>
              <div className="text-gray-500 text-xs mb-0.5">Product Name</div>
              <div className={BORDER_BOX}>{productDetails.productName || '—'}</div>
            </div>
            <div>
              <div className="text-gray-500 text-xs mb-0.5">Category</div>
              <div className={BORDER_BOX}>{productDetails.category || '—'}</div>
            </div>
            <div>
              <div className="text-gray-500 text-xs mb-0.5">SKU (Stock Keeping Unit)</div>
              <div className={BORDER_BOX}>{productDetails.sku || '—'}</div>
            </div>
            <div>
              <div className="text-gray-500 text-xs mb-0.5">Product Type</div>
              <div className={BORDER_BOX}>
                {productType
                  ? productType.charAt(0).toUpperCase() + productType.slice(1)
                  : productDetails.type || '—'}
              </div>
            </div>
            {productType !== 'material' && (
              <div>
                <div className="text-gray-500 text-xs mb-0.5">Cost Price</div>
                <div className={BORDER_BOX}>{productDetails.cost ?? productDetails.price ?? '—'}</div>
              </div>
            )}
            <div>
              <div className="text-gray-500 text-xs mb-0.5">Unit</div>
              <div className={BORDER_BOX}>{productDetails.unit || '—'}</div>
            </div>
            <div>
              <div className="text-gray-500 text-xs mb-0.5">Barcode</div>
              <div className={BORDER_BOX}>{productDetails.barcode || '—'}</div>
            </div>
            <div>
              <div className="text-gray-500 text-xs mb-0.5">Select Site</div>
              <div className={BORDER_BOX}>
                {selectedSite?.site_name || selectedSite?.name || '—'}
              </div>
            </div>
            <div className="col-span-2">
              <div className="text-gray-500 text-xs mb-0.5">Add this to menu</div>
              <div className={BORDER_BOX}>{addToMenu ? 'Yes' : 'No'}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Bill of Materials */}
      <div className="product-template-pdf-section mb-4">
        <div className={SECTION_HEADER}>Bill of Materials</div>
        <table className="w-full border-collapse border border-gray-300 bg-white">
          <thead className="product-template-pdf-bom-thead">
            <tr className="bg-gray-100">
              <th className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700">
                Raw Material
              </th>
              <th className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700">
                Quantity
              </th>
              <th className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700">
                Unit
              </th>
              <th className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700">
                Calories
              </th>
              <th className="border border-gray-300 px-3 py-2 text-left text-xs font-semibold text-gray-700">
                Specifications
              </th>
            </tr>
          </thead>
          <tbody>
            {bomEntries
              .filter((e) => e.raw_material_id && e.quantity && e.unit)
              .map((entry, i) => (
                <tr key={i}>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    {entry.raw_material_name ||
                      getRawMaterialName(entry.raw_material_id) ||
                      entry.raw_material_id}
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    {entry.quantity}
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    {entry.unit}
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    {entry.calories ?? '—'}
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-sm">
                    {entry.specs ?? '—'}
                  </td>
                </tr>
              ))}
            {bomEntries.filter((e) => e.raw_material_id && e.quantity).length === 0 && (
              <tr>
                <td colSpan={5} className="border border-gray-300 px-3 py-4 text-sm text-gray-500 text-center">
                  No BOM entries
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div className="border border-t-0 border-gray-300 p-4 bg-white">
          <div className="text-gray-500 text-xs mb-0.5">
            Total Calories (full recipe)
          </div>
          <div className={BORDER_BOX}>{totalCalories.toFixed(2)} kcal</div>
          {(showPerServe || showCaloriesPerServe) && (
            <div className="mt-3 grid gap-3 sm:grid-cols-2 text-sm">
              {showPerServe && (
                <div>
                  <div className="text-gray-500 text-xs mb-0.5">Per Serve</div>
                  <div className={BORDER_BOX}>{perServe}</div>
                </div>
              )}
              {showCaloriesPerServe && (
                <div>
                  <div className="text-gray-500 text-xs mb-0.5">
                    Calories Per Serve
                  </div>
                  <div className={BORDER_BOX}>
                    {Number(caloriesPerServe).toFixed(2)} kcal
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* SOP (Standard Operating Procedure) – flexible procedures */}
      <div className="mb-4">
        <div className={SECTION_HEADER}>SOP (Standard Operating Procedure)</div>
        {sopProcedures && sopProcedures.length > 0 ? (
          <>
            {sopProcedures.map((proc, idx) => (
  <div key={proc.id || idx} className="product-template-pdf-section">
    <div className={SOP_SUB_HEADER}>
      {proc.heading ? `${idx + 1}. ${proc.heading}` : `${idx + 1}. Procedure`}
    </div>
    <div className="border border-t-0 border-gray-300 p-4 bg-white">
      <div className="text-sm">
        {sopLines(proc.points).length > 0 ? (
          <ul className="list-disc pl-5 space-y-1">
            {sopLines(proc.points).map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        ) : (
          <span className="text-gray-500">—</span>
        )}
      </div>
    </div>
  </div>
))}
          
          </>
        ) : (
          <div className="border border-t-0 border-gray-300 p-4 bg-white">
            <span className="text-gray-500 text-sm">—</span>
          </div>
        )}
      </div>

      {/* Description */}
      <div className="product-template-pdf-section mb-4">
        <div className={SECTION_HEADER}>Description</div>
        <div className={`border border-t-0 border-gray-300 p-4 bg-white ${BORDER_BOX}`}>
          {description || '—'}
        </div>
      </div>

      {/* Allergen Items */}
      <div className="product-template-pdf-section mb-4">
        <div className={SECTION_HEADER}>Allergen Items</div>
        <div className="border border-t-0 border-gray-300 p-4 bg-white text-sm">
          {allergenItems.length > 0 ? (
            allergenData?.categorized && Object.keys(allergenData.categorized).length > 0 ? (
              <div className="space-y-2">
                {Object.entries(allergenData.categorized).map(([cat, items]) => (
                  <div key={cat}>
                    <span className="font-semibold text-gray-700">{cat}: </span>
                    {(items || []).map((item, i) => (
                      <span key={i}>
                        {typeof item === 'string' ? item : item?.name ?? item}
                        {i < items.length - 1 ? ', ' : ''}
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <ul className="list-disc pl-5">
                {allergenItems.map((a, i) => (
                  <li key={i}>{typeof a === 'string' ? a : a?.name ?? '—'}</li>
                ))}
              </ul>
            )
          ) : (
            <span className="text-gray-500">—</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrintProductTemplatePdf;
